import { useEffect, useRef } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import useAuth from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { FcmToken } from '@/integrations/capacitor/FcmToken';

type PushProvider = 'fcm';

type RegisterOptions = {
  platform: 'android' | 'ios' | 'web';
  deviceId?: string;
};

export default function usePushNotifications(opts: RegisterOptions) {
  const { user } = useAuth();
  const registeringRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    try {
      // Guard: only run on native platforms with available plugin
      if (!Capacitor.isNativePlatform()) return;
      if (!Capacitor.isPluginAvailable('PushNotifications')) return;
    } catch (e) {
      console.warn('Capacitor not available; skipping push setup');
      return;
    }

    if (!user?.id) return;
    if (registeringRef.current) return;
    registeringRef.current = true;

    const setup = async () => {
      try {
        const permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive !== 'granted') {
          const req = await PushNotifications.requestPermissions();
          if (req.receive !== 'granted') return;
        }

        // Ensure an Android notification channel exists (Android 8+)
        if (opts.platform === 'android') {
          try {
            await PushNotifications.createChannel?.({
              id: 'giftmap-default',
              name: 'imata',
              description: 'Gift notifications',
              importance: 4,
              visibility: 1,
              lights: true,
              vibration: true,
            } as any);
          } catch (e) {
            console.warn('Android notification channel creation failed:', e);
          }
        }

        await PushNotifications.register();

        const regHandler = await PushNotifications.addListener('registration', async (token) => {
          try {
            let t = token?.value;
            // On iOS, prefer the FCM token if available
            if (opts.platform === 'ios') {
              try {
                const { token: fcm } = await FcmToken.getToken();
                if (fcm) t = fcm;
              } catch (e) {
                console.warn('FCM token fetch failed; falling back to APNs token', e);
              }
            }
            if (!t) return;
            await supabase
              .from('user_push_tokens')
              .upsert(
                {
                  user_id: user.id,
                  token: t,
                  platform: opts.platform,
                  provider: 'fcm' as PushProvider,
                  device_id: opts.deviceId || null,
                  last_used_at: new Date().toISOString(),
                },
                { onConflict: 'user_id,token' }
              );
          } catch (e) {
            console.warn('Push token upsert failed:', e);
          }
        });

        const errHandler = await PushNotifications.addListener('registrationError', (err) => {
          console.warn('Push registration error:', err);
        });

        const recvHandler = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          const title = (notification as any)?.title || 'Notification';
          const body = (notification as any)?.body || '';
          console.log('Push received:', title, body);
          try {
            toast({ title, description: body });
          } catch {}
        });

        return () => {
          regHandler.remove();
          errHandler.remove();
          recvHandler.remove();
        };
      } catch (e) {
        console.warn('Push setup failed:', e);
      } finally {
        registeringRef.current = false;
      }
    };

    const cleanup = setup();
    return () => {
      Promise.resolve(cleanup).then((c: any) => c && c());
    };
  }, [user?.id, opts.platform, opts.deviceId]);
}
